/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { deviceNavigationState } from "../navigation/deviceNavigation";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { motion } from "framer-motion";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant, useUserEmail } from "hooks/settings";
import { Link, useLocation } from "react-router";
import { ScrapEyeDevice } from "src/store/api/generatedApi";
import DailyVideoStaples from "./DailyVideoStaples";
import { DeviceVideoPreview } from "./DeviceVideoPreview";
import StorageProgressBar from "./StorageProgressBar";
import { StandbyFlag } from "./StandbyFlag";

dayjs.extend(relativeTime);

const formatLastSeen = (
  timestamp: string | null | undefined
): string | null => {
  if (!timestamp) return null;
  return dayjs(timestamp).fromNow(true);
};

const GRAFANA_NODE_EXPORTER_URL =
  "https://grafana.monitoring.k8s.gcp.fdm.zone/d/scrap-eye-node-exporter/node-exporter-nodes";
const FDM_EMAIL_DOMAIN = "@foresightdatamachines.com";

const buildGrafanaNodeExporterUrl = (
  tenant: string,
  cluster: string
): string => {
  const params = new URLSearchParams({
    "var-tenant": tenant,
    "var-cluster": cluster,
    "var-instance": cluster,
  });
  return `${GRAFANA_NODE_EXPORTER_URL}?${params.toString()}`;
};

const formatSeconds = (seconds: number | null | undefined): string => {
  if (seconds == null || seconds <= 0) return "-";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) {
    return `${days}d ${hours}h`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

const formatBytes = (
  bytes: number | null | undefined,
  decimals?: number
): string => {
  if (bytes == null || bytes <= 0) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let unitIndex = 0;
  let value = bytes;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  const finalDecimals = decimals ?? (value >= 100 ? 0 : 1);
  return `${value.toFixed(finalDecimals)} ${units[unitIndex] ?? ""}`;
};

const formatCelsius = (celsius: number | null | undefined): string => {
  if (celsius == null) return "-";
  return `${celsius.toFixed(1)}°C`;
};

const formatPercent = (percent: number | null | undefined): string => {
  if (percent == null) return "-";
  return `${percent.toFixed(0)}%`;
};

const formatMilliseconds = (ms: number | null | undefined): string => {
  if (ms == null) return "-";
  return `${(ms / 1000).toFixed(1)}s`;
};

const formatByUnit = (value: number | null, unit: string): string => {
  if (value == null) return "-";

  switch (unit) {
    case "seconds":
      return formatSeconds(value);
    case "bytes":
      return formatBytes(value);
    case "celsius":
      return formatCelsius(value);
    case "percent":
      return formatPercent(value);
    case "milliseconds":
      return formatMilliseconds(value);
    default:
      return String(value);
  }
};

type MetricStatus = "missing" | "ok" | "warning" | "critical";

interface MetricChipProps {
  value: string | number | null | undefined;
  label: string;
  icon?: React.ReactNode;
  status?: MetricStatus;
  tooltip?: string;
  cluster: string;
}

const getStatusStyles = (status: MetricStatus, theme: Theme) => {
  switch (status) {
    case "missing":
      return {
        bgcolor: "grey.100",
        opacity: 0.6,
        labelColor: theme.palette.text.disabled,
        valueColor: theme.palette.text.disabled,
        iconOpacity: 0.4,
        dotColor: theme.palette.grey[400],
        dotSize: 6,
      };
    case "ok":
      return {
        bgcolor: "grey.50",
        opacity: 1,
        labelColor: theme.palette.text.primary,
        valueColor: theme.palette.text.secondary,
        iconOpacity: 1,
        dotColor: theme.palette.success.main,
        dotSize: 6,
      };
    case "warning":
      return {
        bgcolor: "grey.50",
        opacity: 1,
        labelColor: theme.palette.text.primary,
        valueColor: theme.palette.text.secondary,
        iconOpacity: 1,
        dotColor: theme.palette.warning.main,
        dotSize: 8,
      };
    case "critical":
      return {
        bgcolor: "grey.50",
        opacity: 1,
        labelColor: theme.palette.text.primary,
        valueColor: theme.palette.text.secondary,
        iconOpacity: 1,
        dotColor: theme.palette.error.main,
        dotSize: 10,
      };
  }
};

interface MetricChipContentProps {
  styles: ReturnType<typeof getStatusStyles>;
  label: string;
  icon?: React.ReactNode;
  value: string | number | null | undefined;
  tooltip?: string;
  clickable: boolean;
}

const MetricChipContent = ({
  styles,
  label,
  icon,
  value,
  tooltip,
  clickable,
}: MetricChipContentProps) => (
  <Box
    title={tooltip}
    sx={{
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      p: 1,
      minWidth: 80,
      bgcolor: styles.bgcolor,
      margin: "-0.5px",
      border: "1px solid",
      borderColor: "grey.200",
      opacity: styles.opacity,
      transition: "all 0.2s ease",
      cursor: clickable ? "pointer" : undefined,
    }}
  >
    <Box
      sx={{
        position: "absolute",
        bottom: 2,
        right: 2,
        left: 2,
        height: 3,
        bgcolor: styles.dotColor,
        transition: "all 0.2s ease",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        top: 2,
        right: 2,
        left: 2,
        height: 3,
        bgcolor: styles.dotColor,
        transition: "all 0.2s ease",
      }}
    />
    <Typography
      variant="body2Mono"
      fontWeight="fontWeightBold"
      fontSize={12}
      letterSpacing={0}
      color={styles.labelColor}
      noWrap
      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
    >
      {label}
    </Typography>
    <Stack direction="row" spacing={1.0} alignItems="center">
      <Typography
        variant="caption"
        fontSize={14}
        noWrap
        sx={{ opacity: styles.iconOpacity }}
      >
        {icon}
      </Typography>
      <Typography
        variant="body2Mono"
        fontWeight="fontWeightSemiBold"
        fontSize={12}
        letterSpacing={0.1}
        color={styles.valueColor}
        noWrap
      >
        {value ?? "-"}
      </Typography>
    </Stack>
  </Box>
);

const InstallationLocationChip = ({
  location,
}: {
  location: string | null | undefined;
}) => {
  if (!location) return null;
  return <Chip label={location} size="small" variant="outlined" />;
};

const MetricChip = ({
  value,
  label,
  icon,
  status = "ok",
  tooltip,
  cluster,
}: MetricChipProps) => {
  const theme = useTheme();
  const tenantName = useTenant();
  const email = useUserEmail();
  const styles = getStatusStyles(status, theme);

  const isFdmUser = email?.endsWith(FDM_EMAIL_DOMAIN) ?? false;
  const grafanaUrl =
    isFdmUser && cluster
      ? buildGrafanaNodeExporterUrl(tenantName, cluster)
      : null;

  const chip = (
    <MetricChipContent
      styles={styles}
      label={label}
      icon={icon}
      value={value}
      tooltip={tooltip}
      clickable={grafanaUrl != null}
    />
  );

  if (grafanaUrl == null) {
    return chip;
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(grafanaUrl, "_blank", "noopener,noreferrer");
      }}
      sx={{
        textDecoration: "none",
        color: "inherit",
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        font: "inherit",
      }}
    >
      {chip}
    </Box>
  );
};

export function ScrapEyeDataDashboardDevice({
  device,
  isFetching,
  isOnlineExpected,
}: {
  device: ScrapEyeDevice;
  isFetching: boolean;
  isOnlineExpected: boolean;
}) {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const info = device.additional_info;
  const location = useLocation();
  const navigationState = deviceNavigationState({ location, tenantName });
  const deviceUrl = `/${tenantName}/admin/scrap-eye/data-dashboard/device/${encodeURIComponent(device.name)}/info`;

  if (!isOnlineExpected) {
    const lastSeen = formatLastSeen(info?.last_video_segment_received_at);
    return (
      <Link
        to={deviceUrl}
        state={navigationState}
        key={device.id}
        style={{ textDecoration: "none" }}
      >
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              opacity: 0.6,
              backgroundColor: "grey.100",
              "&:hover": { opacity: 0.8 },
              maxWidth: "874px",
            }}
            elevation={0}
          >
            <Stack spacing={1} direction="row" justifyContent="space-between">
              <Stack spacing={1}>
                <Typography
                  variant="h6"
                  fontWeight="fontWeightMedium"
                  color="text.secondary"
                >
                  {device.name ?? t("deviceLabelWithHash", { id: device.id })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("lastSeen")} {lastSeen ?? t("unknown")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("deviceLabelWithHash", { id: device.id })}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <InstallationLocationChip
                    location={device.installation_location}
                  />
                  <StandbyFlag standbyState={device.standby_state} />
                </Stack>
              </Stack>
              <Stack spacing={1}>
                <Typography>{t("coverageByDayLast7Days")}</Typography>
                <DailyVideoStaples
                  dailyVideoData={info?.daily_video_data ?? []}
                />
              </Stack>
            </Stack>
          </Paper>
        </motion.div>
      </Link>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 1,
          maxWidth: "874px",
          border: 1,
          borderColor: "grey.300",
        }}
      >
        <Stack spacing={2}>
          <Box sx={{ display: "flex", flexWrap: "wrap", spacing: 2 }}>
            <Stack spacing={2} sx={{ flex: 1, minWidth: "450px" }}>
              <Stack>
                <Link
                  to={deviceUrl}
                  state={navigationState}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <Typography variant="h5" fontWeight="fontWeightSemiBold">
                    {device.name ?? t("deviceLabelWithHash", { id: device.id })}
                  </Typography>
                </Link>
                {device.name ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("deviceLabelWithHash", { id: device.id })}
                  </Typography>
                ) : null}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 0.5 }}
                >
                  <InstallationLocationChip
                    location={device.installation_location}
                  />
                  <StandbyFlag standbyState={device.standby_state} />
                </Stack>
              </Stack>

              <Box sx={{ width: "fit-content" }}>
                <StorageProgressBar
                  usedGb={info?.storage_space_used_gb ?? null}
                  totalGb={info?.storage_space_total_gb ?? null}
                  isFetching={isFetching}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  {t("coverageByDayLast7Days")}
                </Typography>
                <DailyVideoStaples
                  dailyVideoData={info?.daily_video_data ?? []}
                />
              </Box>
            </Stack>

            <Box
              sx={{
                width: "auto",
                minWidth: "100px",
                maxWidth: { md: "40%", xs: "100%" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
              }}
            >
              <Link
                to={deviceUrl}
                state={navigationState}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <DeviceVideoPreview
                  mp4Path={info?.last_video_segment_mp4_path}
                  label={
                    device.name ?? t("deviceLabelWithHash", { id: device.id })
                  }
                />
              </Link>
            </Box>
          </Box>

          <Stack direction="row" flexWrap="wrap" useFlexGap>
            {info?.metrics?.map((metric) => (
              <MetricChip
                key={metric.key}
                icon={metric.icon}
                value={formatByUnit(metric.value, metric.unit)}
                label={t(metric.translation_key)}
                status={metric.status}
                tooltip={
                  metric.tooltip_key
                    ? t(metric.tooltip_key, metric.tooltip_params ?? {})
                    : undefined
                }
                cluster={device.name}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>
    </motion.div>
  );
}
